import { AxiosError } from 'axios';
import {
  createWebHistory,
  createRouter,
} from 'vue-router';

import { client } from '@/repositories/axios';
import { AuthenticationRepository } from '@/repositories/generated';
import routes from '@/router/routes';
import {
  useLoginUserStore,
  useRootStore,
} from '@/store';
import { ErrorBody } from '@/types';
import { hasAuthority } from '@/utils';

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
  scrollBehavior: (to, from, savedPosition) => {
    const top = { top: 0 };

    // 確認画面から遷移（ブラウザバック等）する場合はページトップを表示
    if (from.path.includes('/confirm')) {
      return top;
    }
    // 表示していたページの位置情報があれば途中から表示
    return savedPosition || top;
  },
});

router.onError((error, to, from) => {
  logger.log({ error, to, from }, 'UncaughtVueRouterError');
  throw error;
});

router.beforeEach(async (to) => {
  logger.log({ to }, 'RouteBeforeEach');

  // 末尾のスラッシュ削除
  const trailingSlashRegex = /^\/.*?\/$/;
  if (trailingSlashRegex.test(to.path)) {
    return {
      ...to,
      path: to.path.replace(trailingSlashRegex, ''),
    };
  }

  /**
   * 認証チェック
   * 認証されていなければログイン画面に遷移
   */

  // 認証必須チェック
  const {
    isAuthRequired,
    requiredAuthority,
  } = to.meta;
  if (!isAuthRequired) return true;

  // --- 認証必須ルート ---
  // ユーザ情報取得（リクエスト数低減処理）
  const loginUser = await useLoginUserStore()
    .getLoginUser();
  const hasAuthorityParams = {
    authority: requiredAuthority,
    userAuthorities: loginUser.authorities,
  };

  // 認可チェック
  if (requiredAuthority && !hasAuthority(hasAuthorityParams)) {
    logger.log({ to, loginUser }, 'RoutePermissionError');
    return { name: 'Forbidden' };
  }
  return true;
  // --- 認証必須ルート ---
});

router.afterEach((to) => {
  logger.log({ to }, 'RouteAfterEach');

  // ページタイトル設定
  const { title } = to.meta;
  document.title = title ? `${title} - Wallet Station 管理画面` : 'Wallet Station 管理画面';
});

// axios interceptor
client.interceptors.request.use(
  (request) => {
    logger.log({ request }, 'ApiRequestInfo');
    return request;
  },
  (error: unknown) => {
    logger.log({ error }, 'ApiRequestError');
  },
);
client.interceptors.response.use(
  (response) => {
    logger.log({ response }, 'ApiResponseInfo');
    return response;
  },
  async (error: AxiosError<ErrorBody>) => {
    logger.log({ error }, 'ApiResponseError');

    const excludeErrorCodes = [
      // ログインIDかパスワードが正しくありません。
      'ERR-D999-0013',
      // 一定回数以上ログインに失敗したため、一時的にログインできない状態です。60分以上経ってから再度お試しください。
      'ERR-D999-0014',
      // アカウントが無効な状態です。管理者へご連絡の上、再度お試しください。
      'ERR-D999-0015',
      // 確認コードをご確認の上、再度認証してください。
      'ERR-D999-0022',
      // 確認コードの有効期限が切れています。もう一度ログインしなおしてください。
      'ERR-D999-0023',
      // ログインIDかメールアドレスが正しくありません。
      'ERR-D999-0028',
    ];

    switch (error.response?.status) {
      case 400:
        useRootStore().badRequestError = error.response.data;
        break;
      case 401:
        if (!excludeErrorCodes.includes(error.response.data.error.code)) {
          // 401は認証エラーなので、ログアウト処理をしてからログイン画面に遷移する
          await AuthenticationRepository.logout();
          router.push({ name: 'Login' });
        }
        break;
      case 403:
        router.push({ name: 'Forbidden' });
        break;
      case 404:
        router.push({ name: 'NotFound' });
        break;
      case 500:
        router.push({ name: 'InternalServerError' });
        break;
      case 503:
        router.push({ name: 'ServiceUnavailable' });
        break;
      default:
    }

    return Promise.reject(error);
  },
);

export default router;
